import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, MapPin, Zap, Trash2,
  Map, Database, ChevronLeft, ChevronRight, Upload,
  CheckCircle2, XCircle, AlertCircle, FileText, AlertTriangle, Search, ShieldAlert,
  History, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LazyGoogleMap from '../../components/map/LazyGoogleMap';
import AutoDiscoverArcGISDialog from '../../components/parcel/AutoDiscoverArcGISDialog';
import AuditDuplicatesDialog from '../../components/parcel/AuditDuplicatesDialog';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

// Per-county V2 admin page. Full feature parity with the legacy per-county
// page but rebuilt on the new per-county collections.
const AdminParcelV2County = () => {
  const { countyId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const chunkedFileInputRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadTask, setUploadTask] = useState(null);
  // Chunked upload
  const [chunkedUploading, setChunkedUploading] = useState(false);
  const [chunkedProgress, setChunkedProgress] = useState(null);
  // Resume-able sessions: any in-progress chunked uploads for this county
  // that crashed / were left hanging in the last 24h.
  const [resumableSessions, setResumableSessions] = useState([]);
  const [resumeSession, setResumeSession] = useState(null);
  const resumeFileInputRef = useRef(null);

  // Saved uploads (GridFS archive) — every finalized chunked upload for
  // this county is preserved here so admins can reprocess past files
  // without re-uploading the original (often hundreds of MB) shapefile.
  const [savedUploads, setSavedUploads] = useState([]);
  const [savedUploadsBusy, setSavedUploadsBusy] = useState(false);

  // Wipe-recovery banner — set right after a successful "Delete All
  // Parcels" if there's at least one GridFS-archived upload available.
  // Lets the admin one-click restore from the most recent saved file
  // in case they wiped by mistake or want to start fresh from a known
  // good snapshot. Cleared on dismiss or after the reprocess starts.
  const [recoveryOption, setRecoveryOption] = useState(null);

  // ETA tracking — keeps a small history of (timestamp, scanned) samples
  // so we can compute a smoothed rows-per-second over the last ~20s.
  const etaSamplesRef = useRef([]);

  // Parcels paginator
  const [parcelsPage, setParcelsPage] = useState({ page: 1, total: 0, total_pages: 0, parcels: [] });
  const [parcelsLoading, setParcelsLoading] = useState(false);

  // Show-on-map modal
  const [showMap, setShowMap] = useState(false);

  // Preview
  const [previewBust, setPreviewBust] = useState(Date.now());
  const [regenPreview, setRegenPreview] = useState(false);
  const [regenProgress, setRegenProgress] = useState(null);

  // Test query
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Auto-discover ArcGIS URL dialog (only shown when this county is
  // manual-only — i.e. auto-registered from the no-data toast and not
  // yet promoted to an auto-fetch entry).
  const [showDiscover, setShowDiscover] = useState(false);

  // Audit dialog — opens from the "Audit" button in the header. Detects
  // exact-equality duplicate parcels (same lat + lng + address). Result
  // is also persisted server-side so the Parcel Converter page can
  // surface it later.
  const [showAudit, setShowAudit] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}`, { headers: authHeaders() });
    if (res.ok && res.data) {
      setDetail(res.data);
      setError(null);
    } else {
      setError(res.error || `HTTP ${res.status}`);
    }
    setLoading(false);
  }, [countyId]);

  const loadParcels = useCallback(async (page = 1) => {
    setParcelsLoading(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/parcels?page=${page}&limit=50`, {
      headers: authHeaders(),
    });
    if (res.ok && res.data) setParcelsPage(res.data);
    setParcelsLoading(false);
  }, [countyId]);

  useEffect(() => {
    loadDetail();
    loadParcels(1);
  }, [loadDetail, loadParcels]);

  // ---------------- upload flow ----------------
  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Upload ${file.name} (${(file.size / 1024).toFixed(1)} KB) to ${detail?.county_name}?\n\nThis APPENDS rows. Duplicates are auto-deduplicated by lat+lng+address.`)) {
      e.target.value = '';
      return;
    }
    setUploading(true);
    setUploadTask({ status: 'running', message: `Uploading ${file.name}...`, progress: 0 });
    // For multipart we still use XHR to keep the bulletproof body-stream behavior
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/admin/parcel-v2/county/${countyId}/upload`, true);
    const token = localStorage.getItem('token') || localStorage.getItem('admin_token') || '';
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch { /* noop */ }
      if (xhr.status >= 200 && xhr.status < 300 && data?.task_id) {
        pollUploadStatus(data.task_id);
      } else {
        setUploading(false);
        setUploadTask({ status: 'failed', message: data?.detail || `Upload failed (HTTP ${xhr.status})` });
        setMessage({ type: 'error', text: data?.detail || `Upload failed (HTTP ${xhr.status})` });
      }
    };
    xhr.onerror = () => {
      setUploading(false);
      setUploadTask({ status: 'failed', message: 'Network error during upload' });
      setMessage({ type: 'error', text: 'Network error during upload' });
    };
    const formData = new FormData();
    formData.append('file', file);
    xhr.send(formData);
    e.target.value = '';
  };

  const pollUploadStatus = async (taskId) => {
    let attempts = 0;
    const maxAttempts = 600;
    const tick = async () => {
      attempts++;
      const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/upload-status/${taskId}`, { headers: authHeaders() });
      if (res.ok && res.data) {
        setUploadTask(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setUploading(false);
          if (res.data.status === 'completed') {
            setMessage({ type: 'success', text: res.data.message || 'Upload complete' });
            setTimeout(() => { loadDetail(); loadParcels(1); }, 500);
          } else {
            setMessage({ type: 'error', text: res.data.message || 'Upload failed' });
          }
          return;
        }
      }
      if (attempts < maxAttempts) setTimeout(tick, 1500);
      else {
        setUploading(false);
        setMessage({ type: 'error', text: 'Upload polling timed out' });
      }
    };
    setTimeout(tick, 1000);
  };

  // ---------------- chunked upload (for 100MB+ shapefiles) ----------------
  // localStorage key for resume metadata
  const LS_RESUME_KEY = `parcel_v2_resume_${countyId}`;

  const _saveResumeState = (state) => {
    try { localStorage.setItem(LS_RESUME_KEY, JSON.stringify(state)); } catch { /* quota */ }
  };
  const _clearResumeState = () => {
    try { localStorage.removeItem(LS_RESUME_KEY); } catch { /* noop */ }
  };
  const _readResumeState = () => {
    try { return JSON.parse(localStorage.getItem(LS_RESUME_KEY) || 'null'); } catch { return null; }
  };

  // Pretty-format an ETA in seconds: "12s", "3 min", "1h 5m"
  const _formatEta = (s) => {
    if (s == null || !isFinite(s) || s <= 0) return '0s';
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)} min`;
    const hr = Math.floor(s / 3600);
    const min = Math.round((s % 3600) / 60);
    return min > 0 ? `${hr}h ${min}m` : `${hr}h`;
  };

  // Send chunks [fromIndex, totalChunks) for `file` to `uploadId`. Refactored
  // out of the initial upload flow so resume can call it with fromIndex > 0.
  const runChunkedUploadFrom = async (file, uploadId, fromIndex, totalChunks, chunkSize) => {
    setChunkedUploading(true);

    // Pre-flight: confirm the backend session is still in "uploading" state
    // and matches our chunk count. If it doesn't exist OR is in a terminal
    // state, fail fast with a clear message + clear stale localStorage —
    // prevents the "infinite resume loop" where we keep retrying chunk 0
    // against a session that no longer accepts appends.
    if (fromIndex > 0 || true) {
      const preflightRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/status/${uploadId}`, {
        headers: authHeaders(),
      });
      if (!preflightRes.ok) {
        setChunkedUploading(false);
        setChunkedProgress(null);
        _clearResumeState();
        setMessage({
          type: 'error',
          text: `Resume failed (HTTP ${preflightRes.status || 0}): ${preflightRes.error || 'unknown'}. The upload session was not found on the server — please start a fresh upload.`,
        });
        loadResumableSessions();
        return;
      }
      const serverStatus = preflightRes.data?.status;
      const serverReceivedChunks = preflightRes.data?.received_chunks ?? 0;
      const serverExpectedChunks = preflightRes.data?.expected_chunks ?? totalChunks;
      if (serverStatus !== 'uploading') {
        setChunkedUploading(false);
        setChunkedProgress(null);
        _clearResumeState();
        const hint =
          serverStatus === 'completed' ? ' This upload already finished successfully — your data is in the database.' :
          serverStatus === 'failed' && preflightRes.data?.session_path ? ' Use "Resume Processing" in the resume banner to retry the parse phase.' :
          serverStatus === 'failed' ? ' The session has been cleaned up. Please start a fresh upload.' :
          serverStatus === 'aborted' ? ' The upload was aborted. Please start a fresh upload.' :
          serverStatus === 'processing' ? ' The server is currently importing this upload — wait for it to finish or refresh the page.' :
          ' Please start a fresh upload.';
        setMessage({
          type: 'error',
          text: `Resume not possible — session is in status "${serverStatus}".${hint}`,
        });
        loadResumableSessions();
        return;
      }
      // Sync the local fromIndex with the server's actual state. If the
      // server has more chunks than we thought, skip ahead. If it has fewer,
      // back up.
      if (serverReceivedChunks !== fromIndex) {
        // Use the server's value; the saved localStorage state can be stale.
        fromIndex = serverReceivedChunks;
      }
      if (serverExpectedChunks !== totalChunks) {
        totalChunks = serverExpectedChunks;
      }
      if (fromIndex >= totalChunks) {
        // Everything is already uploaded — just finalize.
        setChunkedProgress({ phase: 'finalizing', message: 'All chunks already uploaded — finalizing...', upload_progress: 100 });
        // Fall through to the finalize block at the end of this function.
      }
    }

    // Per-chunk send with one short retry to handle transient network blips.
    const _sendChunkOnce = async (chunkIndex, blob) => {
      const formData = new FormData();
      formData.append('chunk', blob, `chunk_${chunkIndex}`);
      formData.append('chunk_index', String(chunkIndex));
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/append/${uploadId}`, true);
        const token = localStorage.getItem('token') || localStorage.getItem('admin_token') || '';
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          const ok = xhr.status >= 200 && xhr.status < 300;
          // Capture the response body so we can show a useful error.
          let detail = '';
          try {
            const parsed = JSON.parse(xhr.responseText || '{}');
            detail = parsed.detail || '';
          } catch { /* non-JSON */ }
          resolve({ ok, status: xhr.status, detail });
        };
        xhr.onerror = () => resolve({ ok: false, status: 0, detail: 'Network error' });
        xhr.send(formData);
      });
    };

    for (let i = fromIndex; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      // First attempt
      let result = await _sendChunkOnce(i, blob);
      // One-shot retry for transient errors (5xx, network, timeout)
      const transient = !result.ok && (result.status === 0 || result.status >= 500);
      if (transient) {
        await new Promise((r) => setTimeout(r, 1500));
        result = await _sendChunkOnce(i, blob);
      }

      if (!result.ok) {
        setChunkedUploading(false);
        // Terminal-state errors → clear localStorage so we don't loop forever.
        const sessionDead =
          result.status === 400 && (
            /Unknown upload_id/i.test(result.detail) ||
            /Cannot append to session in status/i.test(result.detail)
          );
        if (sessionDead) {
          _clearResumeState();
          setMessage({
            type: 'error',
            text: `Resume failed: ${result.detail}. The session is no longer valid — please start a fresh upload.`,
          });
        } else {
          // Transient failure — persist progress so the user can retry.
          _saveResumeState({
            upload_id: uploadId,
            filename: file.name,
            file_size: file.size,
            file_last_modified: file.lastModified,
            file_format: (file.name.split('.').pop() || '').toLowerCase(),
            total_chunks: totalChunks,
            chunk_size: chunkSize,
            next_chunk_index: i,
            saved_at: Date.now(),
          });
          let hint = '';
          if (result.status === 401) hint = ' Your admin session expired — log out and back in.';
          else if (result.status === 0) hint = ' Network error — check connectivity and try Resume Upload again.';
          else if (result.status >= 500) hint = ' Server error — try Resume Upload again in a moment.';
          setMessage({
            type: 'error',
            text: `Chunk ${i + 1}/${totalChunks} failed (HTTP ${result.status}): ${result.detail || 'no detail'}.${hint}`,
          });
        }
        setChunkedProgress(null);
        loadResumableSessions();
        return;
      }
      const pctUpload = Math.round(((i + 1) / totalChunks) * 100);
      setChunkedProgress({
        phase: 'uploading',
        message: `Uploading chunk ${i + 1} of ${totalChunks} (${pctUpload}%)...`,
        upload_progress: pctUpload,
      });
      // Persist progress every chunk so even a hard browser kill is recoverable
      _saveResumeState({
        upload_id: uploadId,
        filename: file.name,
        file_size: file.size,
        file_last_modified: file.lastModified,
        file_format: (file.name.split('.').pop() || '').toLowerCase(),
        total_chunks: totalChunks,
        chunk_size: chunkSize,
        next_chunk_index: i + 1,
        saved_at: Date.now(),
      });
    }

    // Finalize
    setChunkedProgress({ phase: 'finalizing', message: 'Upload complete. Parsing & importing in background...', upload_progress: 100 });
    const finRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/finalize/${uploadId}`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!finRes.ok) {
      setChunkedUploading(false);
      setChunkedProgress(null);
      setMessage({ type: 'error', text: `Finalize failed: ${finRes.error}` });
      return;
    }

    // Upload + finalize succeeded — resume state is no longer useful
    _clearResumeState();

    // Poll status during the import phase
    pollProcessingStatus(uploadId);
  };

  // Reusable processing-phase poller. Used by both the initial finalize flow
  // and the "Resume Processing" button.
  const pollProcessingStatus = (uploadId) => {
    setChunkedUploading(true);
    etaSamplesRef.current = []; // reset rate history for this run
    let polls = 0;
    const maxPolls = 1200;
    const tick = async () => {
      polls++;
      const sRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/upload-status/${uploadId}`, { headers: authHeaders() });
      if (sRes.ok && sRes.data) {
        // Compute a determinate progress estimate for the processing phase
        // using imported_count / expected_total (the county's configured
        // expected_total). When unknown, falls back to indeterminate.
        const imported = sRes.data.imported_count || 0;
        const scanned = sRes.data.scanned_count || 0;
        const expectedTotal = detail?.expected_total || 0;
        // Use scanned (rows read) instead of imported (rows flushed) for the
        // progress estimate during the parse phase. Imported only ticks every
        // 1000 rows; scanned ticks every row → much smoother bar motion for
        // huge files where the first batch flush takes 30-60s.
        const progressCounter = Math.max(scanned, imported);
        const processingPct = expectedTotal > 0
          ? Math.min(100, Math.round((progressCounter / expectedTotal) * 100))
          : null;

        // -- ETA estimation --
        // Keep up to ~20 seconds of samples and use the oldest one inside
        // that window as the regression anchor → robust against jitter at
        // the very start (parser warm-up takes ~1-2s) and end (final flush
        // is large but quick).
        const now = Date.now();
        etaSamplesRef.current.push({ t: now, n: progressCounter });
        etaSamplesRef.current = etaSamplesRef.current.filter((s) => now - s.t <= 20000);
        let rowsPerSec = null;
        let etaSeconds = null;
        if (etaSamplesRef.current.length >= 2) {
          const oldest = etaSamplesRef.current[0];
          const newest = etaSamplesRef.current[etaSamplesRef.current.length - 1];
          const dt = (newest.t - oldest.t) / 1000;
          const dn = newest.n - oldest.n;
          if (dt > 0.5 && dn > 0) {
            rowsPerSec = dn / dt;
            if (expectedTotal > progressCounter) {
              etaSeconds = (expectedTotal - progressCounter) / rowsPerSec;
            }
          }
        }

        setChunkedProgress({
          phase: sRes.data.status,
          message: sRes.data.message || 'Importing...',
          upload_progress: 100,
          processing_progress: processingPct,
          imported_count: imported,
          scanned_count: scanned,
          skipped_count: sRes.data.skipped_count,
          expected_total: expectedTotal,
          rows_per_sec: rowsPerSec,
          eta_seconds: etaSeconds,
        });
        if (sRes.data.status === 'completed' || sRes.data.status === 'failed') {
          setChunkedUploading(false);
          etaSamplesRef.current = [];
          if (sRes.data.status === 'completed') {
            setMessage({ type: 'success', text: sRes.data.message || 'Upload complete' });
            setTimeout(() => { loadDetail(); loadParcels(1); loadResumableSessions(); loadSavedUploads(); }, 500);
          } else {
            setMessage({
              type: 'error',
              text: `${sRes.data.message || 'Import failed'}. Click "Resume Processing" in the resume banner above to retry — already-imported rows will be skipped automatically.`,
            });
            setTimeout(() => loadResumableSessions(), 500);
          }
          return;
        }
      }
      if (polls < maxPolls) setTimeout(tick, 2000);
      else {
        setChunkedUploading(false);
        setMessage({
          type: 'error',
          text: 'Import polling timed out. Click "Resume Processing" in the resume banner to continue (idempotent — already-imported rows will be skipped).',
        });
        setTimeout(() => loadResumableSessions(), 500);
      }
    };
    setTimeout(tick, 1500);
  };

  // Re-runs the parser on an existing server-side session.bin. Used when
  // the processing phase failed or timed out — the chunks are still on disk
  // so we don't have to re-upload 200+ MB.
  const handleResumeProcessing = async (uploadId) => {
    setChunkedUploading(true);
    setChunkedProgress({ phase: 'processing', message: 'Retrying parse & import...', upload_progress: 100 });
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/retry-processing/${uploadId}`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) {
      setChunkedUploading(false);
      setChunkedProgress(null);
      setMessage({ type: 'error', text: `Retry failed: ${res.error}` });
      loadResumableSessions();
      return;
    }
    setMessage({ type: 'info', text: 'Re-processing started. Already-imported rows will be skipped via SHA-1 dedup.' });
    pollProcessingStatus(uploadId);
  };

  const handleChunkedFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Upload ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB) to ${detail?.county_name} via CHUNKED uploader?\n\nThe file will be split into 4 MB chunks so it can bypass production reverse-proxy body limits. Same dedup rules apply. Progress is persisted — if anything fails, click Resume to pick up where you left off.`)) {
      e.target.value = '';
      return;
    }
    const filename = file.name;
    const ext = (filename.split('.').pop() || '').toLowerCase();
    if (!['csv', 'geojson', 'json', 'kml', 'xlsx', 'zip'].includes(ext)) {
      setMessage({ type: 'error', text: `Unsupported file type: .${ext}` });
      e.target.value = '';
      return;
    }
    setChunkedUploading(true);
    setChunkedProgress({ phase: 'init', message: 'Opening upload session...', upload_progress: 0 });

    // 1) Compute total chunks first (so init gets the right count up front)
    const chunkSize = 4 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);

    const initRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/init`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename,
        file_format: ext,
        total_bytes: file.size,
        total_chunks: totalChunks,
      }),
    });
    if (!initRes.ok || !initRes.data?.upload_id) {
      setChunkedUploading(false);
      setChunkedProgress(null);
      // Verbose error so production issues can be debugged from the toast
      // alone (status code + body excerpt + tip when the obvious answer is
      // "the browser tab is stale and needs a hard refresh").
      const status = initRes.status || 0;
      let hint = '';
      if (status === 401) hint = ' Your admin session has expired — please log out and log back in.';
      else if (status === 404) hint = ' The endpoint is missing — try a HARD REFRESH (Ctrl/Cmd+Shift+R) to clear cached frontend assets.';
      else if (status === 0) hint = ' Network unreachable — check your connection.';
      else if (status >= 500) hint = ' Server error — try again in a moment, or check backend logs.';
      setMessage({
        type: 'error',
        text: `Init failed (HTTP ${status}): ${initRes.error || 'unknown'}.${hint}`,
      });
      e.target.value = '';
      return;
    }
    const uploadId = initRes.data.upload_id;
    e.target.value = ''; // free the input
    await runChunkedUploadFrom(file, uploadId, 0, totalChunks, chunkSize);
  };

  // Look for in-progress chunked-upload sessions for this county. Combined
  // signal: backend list of `status=uploading` sessions + any local resume
  // metadata stored in localStorage. The user can resume from either.
  const loadResumableSessions = useCallback(async () => {
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/sessions`, {
      headers: authHeaders(),
    });
    setResumableSessions(res.ok && res.data?.sessions ? res.data.sessions : []);
    const local = _readResumeState();
    setResumeSession(local || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countyId]);

  const handleResumeClick = (session) => {
    // We need the user to re-select the file so we have its bytes again
    setMessage({ type: 'info', text: `Pick the same file (${session.filename || 'previously uploaded file'}) to resume.` });
    resumeFileInputRef.current?.click();
  };

  const handleResumeFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Resolve resume target: prefer the localStorage entry (has chunk_size +
    // next_chunk_index), fall back to the backend session list.
    let saved = _readResumeState();
    if (!saved && resumableSessions.length) {
      const s = resumableSessions[0];
      saved = {
        upload_id: s.upload_id,
        filename: s.filename,
        file_size: s.expected_bytes,
        file_format: s.file_format,
        total_chunks: s.expected_chunks,
        chunk_size: Math.ceil(s.expected_bytes / Math.max(s.expected_chunks, 1)),
        next_chunk_index: s.received_chunks || 0,
        saved_at: Date.now(),
      };
    }
    if (!saved) {
      setMessage({ type: 'error', text: 'No resumable session found.' });
      e.target.value = '';
      return;
    }
    if (file.size !== saved.file_size) {
      setMessage({
        type: 'error',
        text: `File size mismatch — selected ${file.size.toLocaleString()} bytes but the resume session expected ${saved.file_size.toLocaleString()} bytes. Pick the exact same file or abort & start fresh.`,
      });
      e.target.value = '';
      return;
    }

    // Re-confirm with the backend in case more chunks have arrived since
    // we persisted the localStorage entry (e.g., another tab finished some).
    const statusRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/status/${saved.upload_id}`, {
      headers: authHeaders(),
    });
    let serverReceived = saved.next_chunk_index;
    if (statusRes.ok && statusRes.data) {
      serverReceived = statusRes.data.received_chunks ?? serverReceived;
    }
    const fromIndex = serverReceived;
    setMessage({
      type: 'info',
      text: `Resuming ${file.name} from chunk ${fromIndex + 1} of ${saved.total_chunks} (${Math.round((fromIndex / saved.total_chunks) * 100)}% already uploaded)...`,
    });
    e.target.value = '';
    await runChunkedUploadFrom(file, saved.upload_id, fromIndex, saved.total_chunks, saved.chunk_size);
  };

  const handleAbortResume = async (uploadId) => {
    if (!window.confirm('Abort this upload session? The partial bytes on the server will be deleted and you will need to start over.')) return;
    await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/chunked-upload/${uploadId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    _clearResumeState();
    setResumeSession(null);
    loadResumableSessions();
    setMessage({ type: 'info', text: 'Upload session aborted.' });
  };

  // Auto-discover resumable sessions on mount
  useEffect(() => {
    loadResumableSessions();
  }, [loadResumableSessions]);

  // ---------------- saved uploads (GridFS archive) ----------------
  const loadSavedUploads = useCallback(async () => {
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/saved-uploads`, {
      headers: authHeaders(),
    });
    setSavedUploads(res.ok && res.data?.files ? res.data.files : []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countyId]);

  useEffect(() => { loadSavedUploads(); }, [loadSavedUploads]);

  const handleReprocessSaved = async (file) => {
    if (!window.confirm(`Reprocess "${file.filename}" (${(file.size_bytes / (1024 * 1024)).toFixed(1)} MB)?\n\nThis will re-run the parser on the saved file — no re-upload needed. Already-imported rows are skipped via SHA-1 dedup.`)) return;
    setSavedUploadsBusy(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/saved-uploads/${file.file_id}/reprocess`, {
      method: 'POST',
      headers: authHeaders(),
    });
    setSavedUploadsBusy(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Reprocess failed: ${res.data?.detail || res.error || 'unknown error'}` });
      return;
    }
    const newId = res.data?.upload_id;
    setMessage({ type: 'info', text: `Reprocessing started (task ${newId}). Watch the import progress card; this can take several minutes.` });
    // Reuse the existing import-status polling by writing a synthetic task
    // record so the UI shows progress for this restored job.
    setUploadTask({ task_id: newId, status: 'processing' });
    // Refresh detail + parcels once it finishes (poll every 5s)
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      const sRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/upload-status/${newId}`, { headers: authHeaders() });
      if (sRes.ok && sRes.data) {
        setUploadTask(sRes.data);
        if (sRes.data.status === 'completed' || sRes.data.status === 'failed') {
          loadDetail();
          loadParcels(1);
          loadSavedUploads();
          return;
        }
      }
      if (attempts < 360) setTimeout(poll, 5000);
    };
    setTimeout(poll, 2000);
  };

  const handleDeleteSaved = async (file) => {
    if (!window.confirm(`PERMANENTLY DELETE saved upload "${file.filename}" (${(file.size_bytes / (1024 * 1024)).toFixed(1)} MB)?\n\nThe original parcel rows already in MongoDB are NOT affected — only the archived source file is removed. You won't be able to reprocess this file without uploading it again.`)) return;
    setSavedUploadsBusy(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/saved-uploads/${file.file_id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    setSavedUploadsBusy(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Delete failed: ${res.data?.detail || res.error || 'unknown error'}` });
      return;
    }
    setMessage({ type: 'info', text: `Saved upload "${file.filename}" deleted.` });
    loadSavedUploads();
  };

  // ---------------- coord audit & cleanup ----------------
  // Detects parcels with out-of-range or missing lat/lng (e.g. State Plane
  // X/Y leftovers from old Parcel Converter runs that pre-date the
  // reprojection fix) and offers a one-click cleanup.
  const [coordAudit, setCoordAudit] = useState(null);
  const [coordAuditBusy, setCoordAuditBusy] = useState(false);

  const runCoordAudit = useCallback(async () => {
    setCoordAuditBusy(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/coord-audit`, {
      headers: authHeaders(),
    });
    setCoordAuditBusy(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Audit failed: ${res.data?.detail || res.error || 'unknown'}` });
      return;
    }
    setCoordAudit(res.data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countyId]);

  useEffect(() => { runCoordAudit(); }, [runCoordAudit]);

  const handleCoordCleanup = async () => {
    if (!coordAudit || coordAudit.bad_coord_parcels === 0) return;
    if (!window.confirm(
      `Permanently delete ${coordAudit.bad_coord_parcels.toLocaleString()} parcel(s) with out-of-range or missing coordinates?\n\n${coordAudit.good_coord_parcels.toLocaleString()} valid parcels will be kept. This cannot be undone.`
    )) return;
    setCoordAuditBusy(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/coord-cleanup`, {
      method: 'POST',
      headers: authHeaders(),
    });
    setCoordAuditBusy(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Cleanup failed: ${res.data?.detail || res.error || 'unknown'}` });
      return;
    }
    setMessage({ type: 'info', text: res.data?.message || 'Cleanup complete.' });
    await runCoordAudit();
    loadDetail();
    loadParcels(1);
  };

  // ---------------- delete ----------------
  const handleClear = async () => {
    if (!window.confirm(`PERMANENTLY DELETE all ${detail?.parcel_count?.toLocaleString() || ''} parcels for ${detail?.county_name}?\n\nThis cannot be undone.`)) return;
    if (!window.confirm('Are you absolutely sure? Click OK to confirm permanent deletion.')) return;
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/parcels`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setMessage({ type: 'success', text: `Deleted ${res.data?.deleted?.toLocaleString() || 'all'} parcels` });
      // Refresh saved uploads first so we know whether to offer recovery.
      // The list is sorted newest-first by the backend, so [0] is the
      // most-recent archived upload — the obvious "undo" candidate.
      const fresh = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/saved-uploads`, {
        headers: authHeaders(),
      });
      const files = fresh.ok && fresh.data?.files ? fresh.data.files : [];
      setSavedUploads(files);
      if (files.length > 0) {
        setRecoveryOption(files[0]);
      }
      loadDetail(); loadParcels(1);
    } else {
      setMessage({ type: 'error', text: `Delete failed: ${res.error}` });
    }
  };

  const handleRecoveryRestore = async () => {
    if (!recoveryOption) return;
    const file = recoveryOption;
    setRecoveryOption(null);
    // Reuse the existing reprocess flow — no confirm prompt because the
    // admin is explicitly clicking the restore CTA.
    setSavedUploadsBusy(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/saved-uploads/${file.file_id}/reprocess`, {
      method: 'POST',
      headers: authHeaders(),
    });
    setSavedUploadsBusy(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Restore failed: ${res.data?.detail || res.error || 'unknown error'}` });
      return;
    }
    const newId = res.data?.upload_id;
    setMessage({ type: 'info', text: `Restoring "${file.filename}" — re-importing in the background. Progress shown below.` });
    setUploadTask({ task_id: newId, status: 'processing' });
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      const sRes = await safeJsonFetch(`${API}/api/admin/parcel-v2/upload-status/${newId}`, { headers: authHeaders() });
      if (sRes.ok && sRes.data) {
        setUploadTask(sRes.data);
        if (sRes.data.status === 'completed' || sRes.data.status === 'failed') {
          loadDetail();
          loadParcels(1);
          loadSavedUploads();
          return;
        }
      }
      if (attempts < 360) setTimeout(poll, 5000);
    };
    setTimeout(poll, 2000);
  };

  // ---------------- test query ----------------
  const handleTestQuery = async () => {
    setTesting(true);
    setTestResult(null);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/test-query`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (res.ok && res.data) {
      setTestResult(res.data);
      setMessage({ type: res.data.ok ? 'success' : 'info', text: res.data.message });
    } else {
      setMessage({ type: 'error', text: `Test failed: ${res.error}` });
    }
    setTesting(false);
  };

  // ---------------- preview regen ----------------
  const handleRegeneratePreview = async () => {
    setRegenPreview(true);
    setRegenProgress({ status: 'starting', progress: 0, message: 'Starting...' });
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/county/${countyId}/regenerate-preview`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok || !res.data?.process_id) {
      setMessage({ type: 'error', text: `Preview regen failed to start: ${res.error}` });
      setRegenPreview(false);
      setRegenProgress(null);
      return;
    }
    const processId = res.data.process_id;
    let attempts = 0;
    const maxAttempts = 240;
    const poll = async () => {
      attempts++;
      const sRes = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/repair-geo-data/status/${processId}`, {
        headers: authHeaders(),
      });
      if (sRes.ok && sRes.data) {
        setRegenProgress(sRes.data);
        if (sRes.data.status === 'completed' || sRes.data.status === 'failed') {
          setRegenPreview(false);
          setMessage({ type: sRes.data.status === 'completed' ? 'success' : 'error', text: sRes.data.message || '' });
          setTimeout(() => { setRegenProgress(null); setPreviewBust(Date.now()); loadDetail(); }, 1000);
          return;
        }
      }
      if (attempts < maxAttempts) setTimeout(poll, 2500);
    };
    setTimeout(poll, 1500);
  };

  if (loading && !detail) {
    return (
      <div className="p-8 flex items-center justify-center" data-testid="county-v2-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  if (error && !detail) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="county-v2-error">
        <Button variant="ghost" onClick={() => navigate('/admin/parcel-data-v2')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
          <p className="font-semibold text-rose-900">Failed to load county detail</p>
          <p className="text-sm text-rose-700 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-parcel-v2-county-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin/parcel-data-v2')} className="text-gray-600" data-testid="back-to-parcel-v2-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> All Counties
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="county-v2-name">{detail.county_name}</h1>
            <p className="text-sm text-gray-500">
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{detail.county_id}</code>
              {detail.state ? ` · ${detail.state}` : ''}
              {detail.city ? ` · ${detail.city}` : ''}
              {' · '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{detail.collection_name}</code>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {detail.has_2dsphere_index ? (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">2dsphere ✓</Badge>
          ) : (
            <Badge className="bg-rose-100 text-rose-800 border border-rose-200">No 2dsphere</Badge>
          )}
          {detail.health_ok ? (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Healthy</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Needs data</Badge>
          )}
          {/* Audit button — runs an exact-duplicate scan on the V2
              collection. Opens a modal with the results + a Wipe &
              Re-fetch CTA when dupes are found. */}
          <Button
            onClick={() => setShowAudit(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="county-v2-audit-btn"
            title="Scan this county's parcel data for exact-equality duplicates (same lat + lng + address)"
          >
            <ShieldAlert className="w-4 h-4 mr-1.5" /> Audit
          </Button>
        </div>
      </div>

      {/* Wipe-recovery banner — shown right after a successful Delete
          All Parcels when the county has at least one GridFS-archived
          upload available. Lets the admin un-do an accidental wipe in
          one click without re-uploading the original 200+ MB file. */}
      {recoveryOption && (
        <div
          className="rounded-lg p-4 bg-amber-50 border border-amber-300 flex items-start gap-3"
          data-testid="wipe-recovery-banner"
        >
          <div className="p-2 rounded-lg bg-amber-100 text-amber-800 shrink-0">
            <History className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900">Need to undo that wipe?</p>
            <p className="text-sm text-amber-800 mt-0.5">
              The most recent saved upload (<span className="font-mono text-xs">{recoveryOption.filename}</span>
              {recoveryOption.size_bytes ? ` · ${(recoveryOption.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
              {recoveryOption.uploaded_at ? ` · ${new Date(recoveryOption.uploaded_at).toLocaleString()}` : ''})
              is still archived in GridFS. Restore it in one click — no re-upload needed.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={handleRecoveryRestore}
              disabled={savedUploadsBusy || uploading || chunkedUploading}
              data-testid="wipe-recovery-restore-btn"
            >
              <Zap className="w-3 h-3 mr-1" /> Restore from saved upload
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRecoveryOption(null)}
              data-testid="wipe-recovery-dismiss-btn"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`rounded-lg p-3 text-sm ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' :
          message.type === 'error' ? 'bg-rose-50 text-rose-900 border border-rose-200' :
          'bg-blue-50 text-blue-900 border border-blue-200'
        }`} data-testid="county-v2-message">
          {message.text}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white" data-testid="kpi-v2-parcel-count">
          <p className="text-blue-100 text-xs uppercase tracking-wide">Parcels</p>
          <p className="text-3xl font-bold mt-1">{(detail.parcel_count || 0).toLocaleString()}</p>
          {detail.coverage_percent != null && (
            <p className="text-blue-200 text-xs mt-1">{detail.coverage_percent}% of expected</p>
          )}
        </div>
        <div className={`rounded-xl p-5 text-white ${detail.parcels_with_location === detail.parcel_count ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-amber-500 to-amber-600'}`} data-testid="kpi-v2-with-location">
          <p className="text-white/80 text-xs uppercase tracking-wide">With location</p>
          <p className="text-3xl font-bold mt-1">{(detail.parcels_with_location || 0).toLocaleString()}</p>
        </div>
        <div className={`rounded-xl p-5 text-white ${detail.parcels_missing_location > 0 ? 'bg-gradient-to-br from-rose-500 to-rose-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`} data-testid="kpi-v2-missing-location">
          <p className="text-white/80 text-xs uppercase tracking-wide">Missing location</p>
          <p className="text-3xl font-bold mt-1">{(detail.parcels_missing_location || 0).toLocaleString()}</p>
        </div>
        <div className={`rounded-xl p-5 text-white ${detail.has_2dsphere_index ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-rose-500 to-rose-600'}`} data-testid="kpi-v2-index">
          <p className="text-white/80 text-xs uppercase tracking-wide">2dsphere index</p>
          <p className="text-3xl font-bold mt-1">{detail.has_2dsphere_index ? 'Present' : 'Missing'}</p>
        </div>
      </div>

      {/* Coverage progress bar */}
      {detail.expected_total > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-coverage-bar">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 text-sm">Coverage</h3>
            <span className="text-sm font-bold tabular-nums">
              {detail.coverage_percent != null ? `${detail.coverage_percent}%` : '—'}
              <span className="text-gray-400 text-xs ml-2 font-normal">
                {(detail.parcel_count || 0).toLocaleString()} / {detail.expected_total.toLocaleString()}
              </span>
            </span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ${
                (detail.coverage_percent || 0) >= 90 ? 'bg-emerald-500' :
                (detail.coverage_percent || 0) >= 50 ? 'bg-blue-500' :
                (detail.coverage_percent || 0) > 0 ? 'bg-amber-500' : 'bg-gray-300'
              }`}
              style={{ width: `${Math.min(detail.coverage_percent || 0, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Source registry status — only renders when this county was
          auto-registered from the no-data toast (i.e. it lives in the
          dynamic registry and has no ArcGIS URL yet). Static manual-only
          counties (e.g. NYC, El Paso) are intentionally excluded because
          their canonical workflow is a shapefile upload — promote() would
          400 on them anyway. */}
      {detail.source_status?.manual_only && detail.source_status?.auto_registered && (
        <div
          className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-5"
          data-testid="county-v2-discover-panel"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-amber-900 flex items-center gap-2">
                <Search className="w-5 h-5" /> Auto-discover ArcGIS URL
              </h3>
              <p className="text-sm text-amber-900 mt-1">
                This county was auto-added when a delivery zone matched its area, but it has no public ArcGIS URL yet.
                Click below to search ArcGIS Hub for a parcel FeatureServer — if one exists, we'll wire it up so
                Parcel Fetcher can pull data on demand. Otherwise upload a shapefile manually below.
              </p>
            </div>
            <Button
              onClick={() => setShowDiscover(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="county-v2-discover-btn"
            >
              <Search className="w-4 h-4 mr-2" /> Auto-discover ArcGIS URL
            </Button>
          </div>
        </div>
      )}

      {/* When the URL is wired up, surface it so admins know they can
          launch a Parcel Fetcher job. */}
      {!detail.source_status?.manual_only && detail.source_status?.arcgis_url && (
        <div
          className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl p-4"
          data-testid="county-v2-arcgis-ready"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-emerald-900 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> ArcGIS endpoint wired up
              </p>
              <p className="text-xs text-emerald-900 mt-1 break-all">
                <code className="bg-emerald-100 px-1.5 py-0.5 rounded text-[11px]">
                  {detail.source_status.arcgis_url}
                </code>
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/parcel-converter')}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="county-v2-go-fetcher-btn"
            >
              <Database className="w-4 h-4 mr-2" /> Run Parcel Fetcher
            </Button>
          </div>
        </div>
      )}

      {/* Upload panel */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5" data-testid="county-v2-upload-panel">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" /> Upload Parcel Data
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Accepts: <strong>CSV, GeoJSON (.geojson/.json), KML, Excel (.xlsx), Shapefile (.zip)</strong>. Rows missing latitude or longitude are skipped. Same file uploaded twice will not create duplicates (dedup by lat+lng+address). Shapefiles in State Plane / non-WGS84 projections are <strong>automatically reprojected to WGS84</strong> when a <code className="bg-blue-100 px-1 rounded text-[10px]">.prj</code> file is present in the .zip.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.geojson,.json,.kml,.xlsx,.zip"
            onChange={handleFileSelected}
            className="hidden"
            data-testid="county-v2-upload-input"
          />
          <input
            ref={chunkedFileInputRef}
            type="file"
            accept=".csv,.geojson,.json,.kml,.xlsx,.zip"
            onChange={handleChunkedFileSelected}
            className="hidden"
            data-testid="county-v2-chunked-upload-input"
          />
          <input
            ref={resumeFileInputRef}
            type="file"
            accept=".csv,.geojson,.json,.kml,.xlsx,.zip"
            onChange={handleResumeFileSelected}
            className="hidden"
            data-testid="county-v2-resume-upload-input"
          />
          <div className="flex flex-col sm:flex-row gap-2 whitespace-nowrap">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || chunkedUploading}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="county-v2-upload-btn"
              title="Best for files under ~50 MB. Sent as a single multipart request."
            >
              {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Choose File & Upload (Small)'}
            </Button>
            <Button
              onClick={() => chunkedFileInputRef.current?.click()}
              disabled={uploading || chunkedUploading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="county-v2-chunked-upload-btn"
              title="Required for files over ~50 MB. Splits the file into 4 MB chunks so it can bypass reverse-proxy body limits in production."
            >
              {chunkedUploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {chunkedUploading ? 'Uploading (chunked)...' : 'Upload Large File (chunked, recommended for production)'}
            </Button>
          </div>
        </div>
        {/* Resume banner — shown when a previously crashed/dropped upload
            for this county is recoverable (either from localStorage or the
            backend's active-session list). */}
        {!chunkedUploading && (resumeSession || resumableSessions.length > 0) && (() => {
          // Prefer the backend session list — it knows whether the file is
          // resume_upload (still uploading chunks) or resume_processing
          // (chunks done, parser needs to retry).
          const backendSession = resumableSessions[0] || {};
          const session = resumeSession || {
            upload_id: backendSession.upload_id,
            filename: backendSession.filename,
            file_size: backendSession.expected_bytes,
            total_chunks: backendSession.expected_chunks,
            next_chunk_index: backendSession.received_chunks || 0,
          };
          const kind = backendSession.resume_kind ||
            (resumeSession ? 'resume_upload' : 'resume_upload');
          const pct = session.total_chunks ? Math.round((session.next_chunk_index / session.total_chunks) * 100) : 0;
          const isProcessing = kind === 'resume_processing';

          // Compute "age" of the session for staleness warning. Prefer the
          // localStorage saved_at (millis) since that reflects the last user
          // activity; fall back to the backend's started_at (ISO string).
          let ageMs = null;
          if (resumeSession?.saved_at) {
            ageMs = Date.now() - resumeSession.saved_at;
          } else if (backendSession.started_at) {
            const t = Date.parse(backendSession.started_at);
            if (!isNaN(t)) ageMs = Date.now() - t;
          }
          const STALE_HOURS = 6;
          const isStale = ageMs != null && ageMs > STALE_HOURS * 3600 * 1000;
          const formatAge = (ms) => {
            const min = Math.round(ms / 60000);
            if (min < 1) return 'just now';
            if (min < 60) return `${min} min ago`;
            const hr = Math.round(min / 60);
            if (hr < 24) return `${hr}h ago`;
            const days = Math.round(hr / 24);
            return `${days}d ago`;
          };

          return (
            <div className={`mt-3 p-3 rounded-lg border-2 ${isStale ? 'bg-rose-50 border-rose-300' : 'bg-amber-50 border-amber-300'}`} data-testid="county-v2-resume-banner">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-bold text-sm ${isStale ? 'text-rose-900' : 'text-amber-900'}`}>
                      {isProcessing ? '⏸ Resume PROCESSING' : '⏸ Resume previous upload'}
                    </p>
                    {ageMs != null && (
                      <Badge
                        className={`text-[10px] ${isStale ? 'bg-rose-200 text-rose-900' : 'bg-amber-200 text-amber-900'} border-0`}
                        data-testid="county-v2-resume-age-badge"
                      >
                        Saved {formatAge(ageMs)}
                      </Badge>
                    )}
                  </div>
                  {isStale && (
                    <p className="text-xs text-rose-800 mt-1 font-medium" data-testid="county-v2-resume-stale-warning">
                      ⚠ This session is older than {STALE_HOURS} hours. The on-disk session file may have been cleaned up or the data may be out of date — we recommend clicking <strong>Abort</strong> and starting a fresh upload.
                    </p>
                  )}
                  <p className={`text-xs mt-1 ${isStale ? 'text-rose-800' : 'text-amber-800'}`}>
                    {isProcessing ? (
                      <>
                        Parse/import of <strong>{session.filename || 'a previous file'}</strong>
                        {session.file_size ? ` (${(session.file_size / (1024 * 1024)).toFixed(1)} MB)` : ''}
                        {' '}was interrupted (timeout or backend restart). The uploaded chunks are still on the server — click <strong>Resume Processing</strong> to re-run the parser. Already-imported rows are skipped automatically via SHA-1 dedup.
                      </>
                    ) : (
                      <>
                        A chunked upload of <strong>{session.filename || 'a previous file'}</strong>
                        {session.file_size ? ` (${(session.file_size / (1024 * 1024)).toFixed(1)} MB)` : ''}
                        {' '}was interrupted at <strong>chunk {session.next_chunk_index} of {session.total_chunks}</strong>
                        {' '}({pct}% uploaded). Pick the same file to continue from where it stopped.
                      </>
                    )}
                  </p>
                  <code className={`text-[10px] px-1.5 py-0.5 rounded mt-1 inline-block ${isStale ? 'text-rose-700 bg-rose-100' : 'text-amber-700 bg-amber-100'}`}>{session.upload_id}</code>
                </div>
                <div className="flex gap-2 shrink-0">
                  {isProcessing ? (
                    <Button
                      size="sm"
                      onClick={() => handleResumeProcessing(session.upload_id)}
                      className={`text-white ${isStale ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                      data-testid="county-v2-resume-processing-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> Resume Processing
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleResumeClick(session)}
                      className={`text-white ${isStale ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                      data-testid="county-v2-resume-btn"
                    >
                      <Upload className="w-4 h-4 mr-1" /> Resume Upload
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAbortResume(session.upload_id)}
                    className={isStale ? 'border-rose-400 text-rose-800 hover:bg-rose-100' : 'border-amber-400 text-amber-800 hover:bg-amber-100'}
                    data-testid="county-v2-abort-resume-btn"
                  >
                    Abort
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
        {uploadTask && (
          <div className={`mt-3 p-3 border rounded-lg text-sm ${uploadTask.is_stuck ? 'bg-amber-50 border-amber-300' : 'bg-white border-blue-200'}`} data-testid="county-v2-upload-progress">
            <p className="font-semibold text-gray-900">{uploadTask.message || '...'}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
              {uploadTask.imported_count != null && <span>Imported: <strong>{uploadTask.imported_count.toLocaleString()}</strong></span>}
              {uploadTask.skipped_count != null && <span>Skipped: <strong>{uploadTask.skipped_count.toLocaleString()}</strong></span>}
              <Badge className={`${uploadTask.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : uploadTask.status === 'failed' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'} border-0`}>
                {uploadTask.status}
              </Badge>
              {uploadTask.is_stuck && (
                <Badge className="bg-amber-100 text-amber-900 border border-amber-300 animate-pulse" data-testid="v2-active-stuck-badge">
                  ⚠ Stuck — no heartbeat
                </Badge>
              )}
            </div>
            {/* Stuck recovery — mirrors the converter UX. Restarts the
                parser against the existing session.bin (or GridFS archive)
                so the user never has to re-upload. */}
            {uploadTask.is_stuck && uploadTask.upload_id && (
              <div className="mt-2 pt-2 border-t border-amber-300 flex flex-wrap items-center gap-2">
                <span className="text-xs text-amber-900">
                  No status update in 2+ minutes — the import worker may have died.
                </span>
                <Button
                  size="sm"
                  onClick={() => handleResumeProcessing(uploadTask.upload_id)}
                  className="bg-amber-600 hover:bg-amber-700 text-white ml-auto"
                  data-testid="v2-active-stuck-retry-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Clean up & retry
                </Button>
              </div>
            )}
          </div>
        )}
        {chunkedProgress && (
          <div className="mt-3 p-3 bg-white border-2 border-blue-300 rounded-lg" data-testid="county-v2-chunked-upload-progress">
            {/* Upload phase bar */}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className="font-semibold text-gray-700">Phase 1 — Upload</span>
                <span className="font-mono text-gray-600">{chunkedProgress.upload_progress || 0}%</span>
              </div>
              <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div className={`h-full transition-all ${chunkedProgress.upload_progress >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${chunkedProgress.upload_progress || 0}%` }} />
              </div>
            </div>
            {/* Processing phase bar — only meaningful after upload finishes */}
            {(chunkedProgress.phase === 'processing' || chunkedProgress.phase === 'finalizing' || chunkedProgress.phase === 'completed' || chunkedProgress.phase === 'failed') && (
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1 text-xs">
                  <span className="font-semibold text-gray-700">Phase 2 — Parse &amp; Import</span>
                  <span className="font-mono text-gray-600">
                    {chunkedProgress.processing_progress != null
                      ? `${chunkedProgress.processing_progress}% (est.)`
                      : chunkedProgress.imported_count != null
                        ? `${chunkedProgress.imported_count.toLocaleString()} imported`
                        : 'starting...'}
                  </span>
                </div>
                <div className="relative w-full h-2 bg-indigo-100 rounded-full overflow-hidden">
                  {chunkedProgress.processing_progress != null ? (
                    <div
                      className={`h-full transition-all duration-500 ${chunkedProgress.phase === 'completed' ? 'bg-emerald-500' : chunkedProgress.phase === 'failed' ? 'bg-rose-500' : 'bg-indigo-500'}`}
                      style={{ width: `${chunkedProgress.processing_progress}%` }}
                    />
                  ) : chunkedProgress.phase === 'completed' ? (
                    <div className="h-full bg-emerald-500 w-full" />
                  ) : chunkedProgress.phase === 'failed' ? (
                    <div className="h-full bg-rose-500 w-full" />
                  ) : (
                    // Indeterminate animation while we don't know the total
                    <div className="absolute inset-y-0 left-0 w-1/3 bg-indigo-500 animate-pulse" />
                  )}
                </div>
                {chunkedProgress.expected_total > 0 && (
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {chunkedProgress.scanned_count > 0 && chunkedProgress.scanned_count !== chunkedProgress.imported_count
                      ? `Scanned ${chunkedProgress.scanned_count.toLocaleString()} · Imported ${(chunkedProgress.imported_count || 0).toLocaleString()} of ~${chunkedProgress.expected_total.toLocaleString()} expected`
                      : `${(chunkedProgress.imported_count || 0).toLocaleString()} of ~${chunkedProgress.expected_total.toLocaleString()} expected`}
                  </p>
                )}
                {/* ETA + throughput readout — appears once we have enough
                    samples to compute a rate (typically 4-6s in). */}
                {chunkedProgress.rows_per_sec != null && chunkedProgress.phase === 'processing' && (
                  <p className="text-[10px] text-indigo-700 font-medium mt-0.5" data-testid="county-v2-eta-readout">
                    {chunkedProgress.eta_seconds != null && chunkedProgress.eta_seconds > 0 ? (
                      <>~{_formatEta(chunkedProgress.eta_seconds)} remaining</>
                    ) : (
                      <>finalizing...</>
                    )}
                    <span className="text-gray-400 ml-1">
                      · {Math.round(chunkedProgress.rows_per_sec).toLocaleString()} rows/sec
                    </span>
                  </p>
                )}
              </div>
            )}
            {/* Status line */}
            <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-800 truncate flex-1">{chunkedProgress.message}</p>
              <Badge className={`${chunkedProgress.phase === 'completed' ? 'bg-emerald-100 text-emerald-800' : chunkedProgress.phase === 'failed' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'} border-0 shrink-0`}>
                {chunkedProgress.phase}
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
              {chunkedProgress.imported_count != null && <span>Imported: <strong>{chunkedProgress.imported_count.toLocaleString()}</strong></span>}
              {chunkedProgress.skipped_count != null && <span>Skipped: <strong>{chunkedProgress.skipped_count.toLocaleString()}</strong></span>}
            </div>
          </div>
        )}
      </div>

      {/* Cached map preview */}
      {detail.parcel_count > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-preview-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Map className="w-4 h-4 text-blue-600" /> Cached Map Preview
              </h3>
              {detail.preview_meta?.generated_at && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Generated {new Date(detail.preview_meta.generated_at).toLocaleString()} · {(detail.preview_meta.dot_count || 0).toLocaleString()} dots sampled from {(detail.preview_meta.total_parcels || 0).toLocaleString()} parcels
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRegeneratePreview}
                disabled={regenPreview}
                size="sm"
                variant="outline"
                data-testid="county-v2-regen-preview-btn"
              >
                {regenPreview ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                {detail.preview_meta ? 'Regenerate' : 'Generate'} Preview
              </Button>
              {detail.bbox && (
                <Button onClick={() => setShowMap(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="county-v2-open-interactive-map-btn">
                  <Map className="w-4 h-4 mr-1" /> Open Interactive Map
                </Button>
              )}
            </div>
          </div>
          {detail.preview_meta ? (
            <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <img
                src={`${API}/api/admin/parcel-v2/county/${detail.county_id}/preview.png?t=${previewBust}&token=${encodeURIComponent(localStorage.getItem('token') || localStorage.getItem('admin_token') || '')}`}
                alt={`Parcel coverage preview for ${detail.county_name}`}
                className="w-full h-auto cursor-pointer"
                onClick={() => setShowMap(true)}
                data-testid="county-v2-preview-image"
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500">
              <Map className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No preview generated yet. Click <strong>Generate Preview</strong> above.</p>
            </div>
          )}
          {regenPreview && regenProgress && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="county-v2-regen-progress">
              <div className="flex items-center justify-between mb-2 text-xs">
                <span className="font-medium text-blue-900">{regenProgress.message || 'Generating...'}</span>
                <span className="font-bold">{regenProgress.progress || 0}%</span>
              </div>
              <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${regenProgress.progress || 0}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions row */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-wrap gap-2">
        <Button
          onClick={handleTestQuery}
          disabled={testing || detail.parcel_count === 0}
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="county-v2-test-query-btn"
        >
          {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          Test ($geoWithin)
        </Button>
        {detail.bbox && (
          <Button variant="outline" onClick={() => setShowMap(true)} data-testid="county-v2-show-map-btn">
            <Map className="w-4 h-4 mr-2" /> Show on Map
          </Button>
        )}
        <Button variant="outline" onClick={loadDetail} data-testid="county-v2-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
        {detail.parcel_count > 0 && (
          <Button variant="ghost" onClick={handleClear} className="text-rose-600 hover:bg-rose-50 ml-auto" data-testid="county-v2-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Delete All Parcels
          </Button>
        )}
      </div>

      {/* Test result panel */}
      {testResult && (
        <div className={`rounded-xl p-4 border ${testResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-testid="county-v2-test-result">
          <p className={`font-semibold text-sm ${testResult.ok ? 'text-emerald-900' : 'text-rose-900'}`}>
            {testResult.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1" /> : <XCircle className="w-4 h-4 inline mr-1" />}
            {testResult.message}
          </p>
          {testResult.sample && testResult.sample.length > 0 && (
            <details className="mt-2 text-xs text-gray-700">
              <summary className="cursor-pointer">Sample parcels returned ({testResult.sample.length})</summary>
              <ul className="mt-1 space-y-1">
                {testResult.sample.map((s, i) => (
                  <li key={i}>{s.address || '(no address)'} — ({s.lat}, {s.lon})</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* bbox + sample */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-bbox-panel">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" /> Bounding Box
          </h3>
          {detail.bbox ? (
            <div className="text-xs font-mono space-y-1 text-gray-700">
              <p>SW: {detail.bbox.min_lat.toFixed(5)}, {detail.bbox.min_lng.toFixed(5)}</p>
              <p>NE: {detail.bbox.max_lat.toFixed(5)}, {detail.bbox.max_lng.toFixed(5)}</p>
              <p>Center: {detail.center?.lat.toFixed(5)}, {detail.center?.lng.toFixed(5)}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No bounding box yet — upload data to populate.</p>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-sample-panel">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-600" /> Sample Parcel
          </h3>
          {detail.sample ? (
            <div className="text-xs space-y-1 text-gray-700">
              <p><strong>Address:</strong> {detail.sample.full_address || `${detail.sample.house_number || ''} ${detail.sample.street_name || ''}`.trim() || '(no address)'}</p>
              <p><strong>Coords:</strong> {detail.sample.latitude}, {detail.sample.longitude}</p>
              <p><strong>Has location:</strong> {detail.sample.location ? '✓' : '✗'}</p>
              <p><strong>Type:</strong> {detail.sample.property_type || '(none)'}</p>
              {detail.sample.imported_at && (
                <p><strong>Imported:</strong> {new Date(detail.sample.imported_at).toLocaleString()}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No data uploaded yet.</p>
          )}
        </div>
      </div>

      {/* last upload */}
      {detail.last_upload && (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-last-upload">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" /> Last Upload
          </h3>
          <div className="text-xs grid grid-cols-2 md:grid-cols-4 gap-3 text-gray-700">
            <div><p className="text-gray-500">When</p><p>{new Date(detail.last_upload.at).toLocaleString()}</p></div>
            <div><p className="text-gray-500">Imported</p><p>{(detail.last_upload.imported || 0).toLocaleString()}</p></div>
            <div><p className="text-gray-500">Skipped</p><p>{(detail.last_upload.skipped || 0).toLocaleString()}</p></div>
            <div><p className="text-gray-500">Format</p><p>{(detail.last_upload.file_format || '').toUpperCase()}</p></div>
          </div>
        </div>
      )}

      {/* Coord Audit & Cleanup — surfaces parcels with bad lat/lng so the
           admin can purge State-Plane junk left over from old converter
           outputs (or any other source of corrupt coordinates). */}
      {coordAudit && coordAudit.total_parcels > 0 && (
        <div
          className={`bg-white border rounded-xl p-5 ${
            coordAudit.bad_coord_parcels > 0 ? 'border-amber-300' : 'border-emerald-200'
          }`}
          data-testid="county-v2-coord-audit"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                {coordAudit.bad_coord_parcels > 0 ? (
                  <>
                    <AlertTriangle className="w-4 h-4 text-amber-600" /> Coordinate Audit — Issues Found
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Coordinate Audit — Healthy
                  </>
                )}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Checks every parcel for lat/lng outside [-90,90] / [-180,180] or missing values.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={runCoordAudit} disabled={coordAuditBusy} data-testid="coord-audit-refresh-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Re-scan
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Total</p>
              <p className="text-lg font-bold text-gray-900">{coordAudit.total_parcels.toLocaleString()}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Valid</p>
              <p className="text-lg font-bold text-emerald-900">{coordAudit.good_coord_parcels.toLocaleString()}</p>
            </div>
            <div className={`rounded-md p-3 border ${
              coordAudit.bad_coord_parcels > 0 ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className={`text-[10px] uppercase tracking-wide font-semibold ${
                coordAudit.bad_coord_parcels > 0 ? 'text-amber-700' : 'text-gray-500'
              }`}>Bad coords</p>
              <p className={`text-lg font-bold ${
                coordAudit.bad_coord_parcels > 0 ? 'text-amber-900' : 'text-gray-900'
              }`}>{coordAudit.bad_coord_parcels.toLocaleString()}</p>
            </div>
          </div>
          {coordAudit.bad_coord_parcels > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-200">
              <p className="text-xs text-amber-900 mb-2">
                Sample bad rows:
              </p>
              <ul className="text-[11px] font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 space-y-0.5 mb-3 max-h-32 overflow-y-auto">
                {(coordAudit.sample_bad_rows || []).map((r, i) => (
                  <li key={i}>
                    lat=<span className="text-amber-700">{String(r.latitude ?? '—')}</span>{' '}
                    lng=<span className="text-amber-700">{String(r.longitude ?? '—')}</span>
                    {r.full_address && <span className="text-gray-500"> · {r.full_address}</span>}
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleCoordCleanup}
                disabled={coordAuditBusy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="coord-cleanup-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete {coordAudit.bad_coord_parcels.toLocaleString()} bad parcel{coordAudit.bad_coord_parcels === 1 ? '' : 's'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Saved Uploads (GridFS archive) — every finalized chunked upload
           is preserved here so admins can reprocess past files without
           re-uploading the original (often 200+ MB) source. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-saved-uploads">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Database className="w-4 h-4 text-emerald-600" /> Saved Uploads
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Persistent archive of every chunked upload for this county. Reprocess any of them without re-uploading — already-imported rows are skipped via SHA-1 dedup.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadSavedUploads} data-testid="saved-uploads-refresh-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
        {savedUploads.length === 0 ? (
          <p className="text-sm text-gray-500 py-3">No saved uploads yet. Finalized chunked uploads will appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Filename</th>
                  <th className="text-right px-3 py-2 font-semibold">Size</th>
                  <th className="text-left px-3 py-2 font-semibold">Format</th>
                  <th className="text-left px-3 py-2 font-semibold">Uploaded</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedUploads.map((f) => (
                  <tr key={f.file_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs text-gray-800" title={f.filename}>{f.filename}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{(f.size_bytes / (1024 * 1024)).toFixed(1)} MB</td>
                    <td className="px-3 py-2 text-gray-700">{(f.file_format || '').toUpperCase()}</td>
                    <td className="px-3 py-2 text-gray-700">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReprocessSaved(f)}
                          disabled={savedUploadsBusy || uploading || chunkedUploading}
                          data-testid={`saved-upload-reprocess-${f.file_id}`}
                        >
                          <Zap className="w-3 h-3 mr-1" /> Reprocess
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteSaved(f)}
                          disabled={savedUploadsBusy}
                          data-testid={`saved-upload-delete-${f.file_id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Browse Parcels paginator */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-v2-parcels-table">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Browse Parcels</h3>
          <p className="text-xs text-gray-500">
            Page {parcelsPage.page} of {parcelsPage.total_pages.toLocaleString()} · {parcelsPage.total.toLocaleString()} parcels
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Address</th>
                <th className="text-left px-3 py-2 font-semibold">Street</th>
                <th className="text-right px-3 py-2 font-semibold">Lat</th>
                <th className="text-right px-3 py-2 font-semibold">Lng</th>
                <th className="text-center px-3 py-2 font-semibold">Loc</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {parcelsLoading ? (
                <tr><td colSpan="6" className="text-center py-6 text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline" /></td></tr>
              ) : parcelsPage.parcels.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-6 text-gray-400">No parcels uploaded yet.</td></tr>
              ) : parcelsPage.parcels.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-700">{p.full_address || p.house_number || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{p.street_name || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{p.lat?.toFixed(5)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{p.lng?.toFixed(5)}</td>
                  <td className="px-3 py-2 text-center">{p.has_location ? '✓' : '✗'}</td>
                  <td className="px-3 py-2 text-gray-500">{p.property_type || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3">
          <Button variant="outline" size="sm" disabled={parcelsPage.page <= 1 || parcelsLoading} onClick={() => loadParcels(parcelsPage.page - 1)} data-testid="v2-parcels-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={parcelsPage.page >= parcelsPage.total_pages || parcelsLoading} onClick={() => loadParcels(parcelsPage.page + 1)} data-testid="v2-parcels-next-btn">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Show on Map modal */}
      {showMap && detail.bbox && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMap(false)} data-testid="county-v2-map-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="county-v2-map-modal">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="font-bold text-gray-900">{detail.county_name} — Bounding Box</h3>
              <button onClick={() => setShowMap(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <div className="min-h-[500px]">
              <LazyGoogleMap
                center={{ lat: detail.center.lat, lng: detail.center.lng }}
                zoom={10}
                style={{ height: '500px', width: '100%' }}
                polygon={[
                  [detail.bbox.min_lat, detail.bbox.min_lng],
                  [detail.bbox.min_lat, detail.bbox.max_lng],
                  [detail.bbox.max_lat, detail.bbox.max_lng],
                  [detail.bbox.max_lat, detail.bbox.min_lng],
                ]}
                polygonOptions={{ fillColor: '#3b82f6', fillOpacity: 0.12, strokeColor: '#3b82f6', strokeWeight: 2 }}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowMap(false)}>Close</Button>
              <Button
                onClick={() => {
                  sessionStorage.setItem('parcel_zoom_to', JSON.stringify({ center: detail.center, county_name: detail.county_name }));
                  navigate('/admin/create-job');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="county-v2-open-create-job-btn"
              >
                <MapPin className="w-4 h-4 mr-1" /> Open in Create Job
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-discover ArcGIS URL dialog */}
      {showDiscover && (
        <AutoDiscoverArcGISDialog
          countyId={detail.county_id}
          countyName={detail.county_name}
          onClose={() => setShowDiscover(false)}
          onPromoted={() => {
            setMessage({ type: 'success', text: 'ArcGIS URL wired up. You can now run Parcel Fetcher for this county.' });
            loadDetail();
          }}
        />
      )}

      {/* Audit duplicates dialog */}
      {showAudit && (
        <AuditDuplicatesDialog
          countyId={detail.county_id}
          countyName={detail.county_name}
          isAutoFetchable={!detail.source_status?.manual_only && !!detail.source_status?.arcgis_url}
          onClose={() => setShowAudit(false)}
        />
      )}
    </div>
  );
};

export default AdminParcelV2County;
